// src/components/legal/PrivacyPolicy.tsx
import Footer from "@/components/shared/Footer";
import {
  ArrowLeft,
  Bot,
  Database,
  Eye,
  Mail,
  Shield,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  const lastUpdated = "September 2025";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-blue-600/20 to-green-600/20"></div>
        <div className="relative max-w-4xl mx-auto px-4 py-16">
          <Link
            to="/"
            className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors mb-8 group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>

          <div className="flex items-center mb-6">
            <Shield className="w-8 h-8 text-green-400 mr-4" />
            <h1 className="text-4xl lg:text-5xl font-bold text-white">
              Privacy Policy
            </h1>
          </div>

          <p className="text-xl text-gray-300 max-w-3xl">
            How we collect, use, and protect your personal information
          </p>
          <p className="text-sm text-gray-400 mt-4">
            Last updated: {lastUpdated} • GDPR Compliant
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 pb-16">
        <div className="bg-slate-800/40 backdrop-blur-sm rounded-xl border border-slate-600/50 p-8 space-y-8">
          {/* Privacy Promise */}
          <div className="bg-green-500/10 rounded-lg p-6 border border-green-500/20">
            <h2 className="text-xl font-semibold text-green-400 mb-4 flex items-center">
              <Shield className="w-5 h-5 mr-2" />
              Our Privacy Promise
            </h2>
            <div className="grid md:grid-cols-2 gap-6 text-sm">
              <div className="space-y-3">
                <div className="flex items-start">
                  <Eye className="w-4 h-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">
                    <strong className="text-white">Transparency:</strong> We're
                    clear about what data we collect and why
                  </span>
                </div>
                <div className="flex items-start">
                  <Users className="w-4 h-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">
                    <strong className="text-white">Your Control:</strong> You
                    can access, update, or delete your data anytime
                  </span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start">
                  <Database className="w-4 h-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">
                    <strong className="text-white">Minimal Collection:</strong>{" "}
                    We only collect what's needed for our services
                  </span>
                </div>
                <div className="flex items-start">
                  <Shield className="w-4 h-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">
                    <strong className="text-white">Secure Storage:</strong> Your
                    data is encrypted and securely stored
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* What We Collect */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              1. Information We Collect
            </h2>

            <div className="space-y-6">
              <div className="bg-slate-700/30 rounded-lg p-4 border border-blue-500/20">
                <h3 className="font-semibold text-blue-400 mb-3 flex items-center">
                  <Database className="w-4 h-4 mr-2" />
                  Culture Lab Analysis Data
                </h3>
                <ul className="space-y-1 text-gray-300 text-sm">
                  <li>
                    • Business type and sector (Property, Hospitality, Retail)
                  </li>
                  <li>
                    • Property details (name, location, size, age, current
                    offerings)
                  </li>
                  <li>• Identified challenges and strategic priorities</li>
                  <li>
                    • Analysis results and recommendations (anonymised for
                    improvement)
                  </li>
                </ul>
              </div>

              <div className="bg-slate-700/30 rounded-lg p-4 border border-purple-500/20">
                <h3 className="font-semibold text-purple-400 mb-3 flex items-center">
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Data
                </h3>
                <ul className="space-y-1 text-gray-300 text-sm">
                  <li>
                    • Email address (for consultations and communications)
                  </li>
                  <li>• Contact form submissions and consultation requests</li>
                  <li>• Response history and engagement metrics</li>
                  <li>• Project briefings and requirements</li>
                </ul>
              </div>

              <div className="bg-slate-700/30 rounded-lg p-4 border border-orange-500/20">
                <h3 className="font-semibold text-orange-400 mb-3 flex items-center">
                  <Bot className="w-4 h-4 mr-2" />
                  Service Usage Data
                </h3>
                <ul className="space-y-1 text-gray-300 text-sm">
                  <li>• MagDrops project specifications and deliverables</li>
                  <li>• Sub-brand consultation notes and strategies</li>
                  <li>• Website usage patterns and feature interactions</li>
                  <li>• Session data for service improvement</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Data */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              2. How We Use Your Information
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-medium text-white">Primary Services:</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Provide Culture Lab analysis and recommendations</li>
                  <li>• Schedule and conduct strategic consultations</li>
                  <li>• Create custom MagDrops marketing materials</li>
                  <li>• Deliver sub-brand strategy and implementation</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="font-medium text-white">Service Improvement:</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Analyse usage patterns to improve our platform</li>
                  <li>• Enhance analysis accuracy and recommendations</li>
                  <li>• Develop new features based on user needs</li>
                  <li>• Send relevant updates and service announcements</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              3. Data Retention Periods
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-600">
                    <th className="text-left py-3 text-white">Data Type</th>
                    <th className="text-left py-3 text-white">
                      Retention Period
                    </th>
                    <th className="text-left py-3 text-white">Reason</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr className="border-b border-slate-700">
                    <td className="py-3">Culture Lab analysis</td>
                    <td className="py-3">2 years</td>
                    <td className="py-3">
                      Service improvement, follow-up consultations
                    </td>
                  </tr>
                  <tr className="border-b border-slate-700">
                    <td className="py-3">Client project data</td>
                    <td className="py-3">7 years</td>
                    <td className="py-3">Business records, warranty support</td>
                  </tr>
                  <tr className="border-b border-slate-700">
                    <td className="py-3">Contact information</td>
                    <td className="py-3">Until requested deletion</td>
                    <td className="py-3">Ongoing business relationship</td>
                  </tr>
                  <tr>
                    <td className="py-3">Analytics & usage</td>
                    <td className="py-3">6 months</td>
                    <td className="py-3">Platform optimization</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* GDPR Rights */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              4. Your Rights (GDPR)
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-700/30 rounded-lg p-4 border border-green-500/20">
                <h3 className="font-semibold text-green-400 mb-2">
                  Access & Portability
                </h3>
                <p className="text-gray-300 text-sm">
                  Request a copy of all data we hold about you in a portable
                  format
                </p>
              </div>

              <div className="bg-slate-700/30 rounded-lg p-4 border border-blue-500/20">
                <h3 className="font-semibold text-blue-400 mb-2">
                  Rectification
                </h3>
                <p className="text-gray-300 text-sm">
                  Correct inaccurate personal data or complete incomplete data
                </p>
              </div>

              <div className="bg-slate-700/30 rounded-lg p-4 border border-red-500/20">
                <h3 className="font-semibold text-red-400 mb-2">
                  Erasure (Right to be Forgotten)
                </h3>
                <p className="text-gray-300 text-sm">
                  Request deletion of your personal data when no longer needed
                </p>
              </div>

              <div className="bg-slate-700/30 rounded-lg p-4 border border-purple-500/20">
                <h3 className="font-semibold text-purple-400 mb-2">
                  Restrict Processing
                </h3>
                <p className="text-gray-300 text-sm">
                  Limit how we use your data in specific circumstances
                </p>
              </div>
            </div>

            <div className="mt-4 p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <p className="text-blue-300 text-sm">
                <strong>Exercise Your Rights:</strong> Contact us at
                hello@thecontentgroup.co We'll respond within 30 days.
              </p>
            </div>
          </section>

          {/* Cookies & Tracking */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              5. Cookies & Tracking
            </h2>
            <div className="prose prose-invert max-w-none text-gray-300">
              <p className="mb-4">
                We use cookies to improve your experience and analyse site
                usage. You can control cookie preferences through our{" "}
                <Link
                  to="/cookie-policy"
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  Cookie Policy
                </Link>{" "}
                and the cookie consent banner.
              </p>

              <div className="bg-slate-700/30 rounded-lg p-4 border border-orange-500/20">
                <h3 className="font-semibold text-orange-400 mb-2">
                  Cookie Types:
                </h3>
                <ul className="space-y-1 text-gray-300 text-sm">
                  <li>
                    • <strong>Essential:</strong> Required for site
                    functionality (always active)
                  </li>
                  <li>
                    • <strong>Analytics:</strong> Help us understand site usage
                  </li>
                  <li>
                    • <strong>Functional:</strong> Remember your preferences and
                    settings
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Contact & Updates */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              6. Contact & Updates
            </h2>
            <div className="prose prose-invert max-w-none text-gray-300">
              <p className="mb-4">
                Questions about this privacy policy or your data? Contact our
                team:
              </p>
              <div className="bg-slate-700/30 rounded-lg p-4 border border-blue-500/20">
                <ul className="space-y-1 text-sm">
                  <li>
                    • <strong className="text-white">Email:</strong>{" "}
                    hello@thecontentgroup.co
                  </li>
                  <li>
                    • <strong className="text-white">Subject:</strong> Privacy
                    Policy Query
                  </li>
                  <li>
                    • <strong className="text-white">Response time:</strong>{" "}
                    Within 30 days
                  </li>
                </ul>
              </div>
              <p className="mt-4 text-sm">
                We'll notify you of significant privacy policy changes via email
                or prominent website notice.
              </p>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-slate-600/50 pt-6">
            <div className="flex items-center justify-between text-sm text-gray-400">
              <div className="flex items-center">
                <Shield className="w-4 h-4 mr-2" />
                GDPR Compliant • Last updated: {lastUpdated}
              </div>
              <div className="flex space-x-4">
                <Link
                  to="/terms-conditions"
                  className="hover:text-white transition-colors"
                >
                  Terms & Conditions
                </Link>
                <Link
                  to="/cookie-policy"
                  className="hover:text-white transition-colors"
                >
                  Cookie Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Your existing Footer */}
      <Footer variant="dark" />
    </div>
  );
}
